import { useEffect, useState } from "react";
import {
  DEFAULT_WORLD_SIZE,
  MIN_CHAR_PX,
  MAX_CHAR_PX,
  npcSizePx,
  type WorldSize,
} from "@greg-chick/shared";
import { getWorldSize, saveWorldSize } from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { Msg } from "./ui";

/**
 * 체험공간 캐릭터 크기 조절(FR-WORLD).
 *
 * "그레그를 조금 더 크게" 같은 조정은 눈으로 보며 맞추는 일이라 코드에 박아두면 매번 배포가 필요했다.
 * 저장하면 지금 체험공간에 있는 사람 화면에도 바로 반영된다(SSE).
 */
const NPCS: { id: string; label: string; hint: string }[] = [
  { id: "greg_white", label: "그레그", hint: "이야기의 주인공. 기본은 히어로보다 조금 큼" },
  { id: "chick", label: "병아리", hint: "그레그의 친구" },
  { id: "greg_cat", label: "검은 고양이", hint: "마을 곳곳에 있는 친구" },
];

/** 크기 하나 — 슬라이더 + 숫자 + 실제 비율 미리보기. */
function SizeRow({
  label,
  hint,
  px,
  heroPx,
  onChange,
}: {
  label: string;
  hint: string;
  px: number;
  heroPx: number;
  onChange: (v: number) => void;
}) {
  const rel = Math.round((px / heroPx) * 100);
  return (
    <label className="wsize__row">
      <span className="wsize__name">
        {label}
        <em>{px}px</em>
      </span>
      <input
        type="range"
        min={MIN_CHAR_PX}
        max={MAX_CHAR_PX}
        step={2}
        value={px}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={`${label} 크기`}
      />
      {/* 실제 비율을 그대로 보여주는 막대 — 숫자만으론 얼마나 큰지 감이 안 온다 */}
      <span className="wsize__bar" aria-hidden="true">
        <i style={{ height: `${(px / MAX_CHAR_PX) * 100}%` }} />
      </span>
      <small>
        {hint} · 히어로의 <b>{rel}%</b>
      </small>
    </label>
  );
}

/**
 * @param only 한 캐릭터만 보여주는 모드([🧸 캐릭터] 화면이 끼워 쓴다).
 *             `"hero"` = 내 히어로, 그 밖엔 NPC id. 나머지 슬라이더는 숨긴다 —
 *             한 친구를 고쳐보러 온 사람에게 네 개를 다 보여주면 어느 걸 만질지 헷갈린다.
 */
export function WorldSizePanel({ only }: { only?: string } = {}) {
  const [size, setSize] = useState<WorldSize>(DEFAULT_WORLD_SIZE);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getWorldSize()
      .then((s) => {
        setSize(s);
        setLoaded(true);
      })
      .catch(() => setMsg("엣지 서버에 연결하지 못했어요. 기본 크기를 보여주고 있어요."));
  }, []);

  async function onSave() {
    const s = await saveWorldSize(size);
    setSize(s);
    setMsg("✅ 저장했어요. 체험공간에 바로 보여요.");
  }
  useRegisterSave({ id: "worldsize", label: "캐릭터 크기", save: onSave });

  const setNpc = (id: string, v: number) =>
    setSize((s) => ({ ...s, npc: { ...s.npc, [id]: v } }));

  const showHero = !only || only === "hero";
  const shownNpcs = only ? NPCS.filter((n) => n.id === only) : NPCS;
  // 목록에 없는 NPC 를 골라 왔으면(커스텀 친구) 그 친구 줄을 만들어 준다.
  if (only && only !== "hero" && !shownNpcs.length) {
    shownNpcs.push({ id: only, label: only, hint: "새로 넣은 친구" });
  }

  return (
    <section className="panel wsize">
      {only ? null : <h2 className="panel__h">체험공간 캐릭터 크기</h2>}
      <p className="hint">
        아이가 마을에서 보는 캐릭터의 <b>키</b>예요. 슬라이더를 움직이고 저장하면 바로 반영돼요.
        너무 크면 화면을 가리고, 너무 작으면 아이가 못 찾아요.
      </p>

      {showHero ? (
        <div className="wsize__group">
          <h3 className="wsize__gh">아이 자신 (히어로)</h3>
          <SizeRow
            label="내 히어로"
            hint="아이가 만든 캐릭터. 다른 사람 화면에도 같은 크기로 보여요"
            px={size.hero}
            heroPx={size.hero}
            onChange={(v) => setSize((s) => ({ ...s, hero: v }))}
          />
          {only ? (
            <p className="hint">
              히어로 크기는 <b>모든 아이 캐릭터가 함께 쓰는 값</b>이에요 — 여기서 바꾸면 완성
              캐릭터도 직접꾸미기도 같이 커져요.
            </p>
          ) : null}
        </div>
      ) : null}

      {shownNpcs.length ? (
        <div className="wsize__group">
          <h3 className="wsize__gh">{only ? "이 친구의 키" : "마을 친구들 (NPC)"}</h3>
          {shownNpcs.map((n) => (
            <SizeRow
              key={n.id}
              label={n.label}
              hint={n.hint}
              px={npcSizePx(size, n.id)}
              heroPx={size.hero}
              onChange={(v) => setNpc(n.id, v)}
            />
          ))}
          {only ? null : (
            <SizeRow
              label="그 밖의 친구"
              hint="위에 없는 캐릭터를 새로 넣었을 때 쓰는 크기"
              px={size.npcDefault}
              heroPx={size.hero}
              onChange={(v) => setSize((s) => ({ ...s, npcDefault: v }))}
            />
          )}
        </div>
      ) : null}

      <Msg text={msg} />

      <div className="wsize__actions">
        <button
          className="btn btn--sm"
          onClick={() => {
            setSize(DEFAULT_WORLD_SIZE);
            setMsg(
              only
                ? "모든 캐릭터를 처음 크기로 되돌렸어요. [저장]을 눌러야 실제로 반영돼요."
                : "처음 크기로 되돌렸어요. [저장]을 눌러야 실제로 반영돼요.",
            );
          }}
          disabled={!loaded}
        >
          처음 크기로 되돌리기
        </button>
      </div>
    </section>
  );
}
